import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MoneyState, SubCreate, SubPatch } from "../../shared/money";
import { api, ApiError } from "./api";

export const moneyKey = ["money"] as const;

export function useMoney() {
  return useQuery({
    queryKey: moneyKey,
    queryFn: api.money,
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
  });
}

/** Pay/skip/unpay all return the recomputed state, so the cache just swaps. */
export function usePayCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["paySub"],
    mutationFn: ({
      id,
      dueDate,
      skipped,
      undo,
    }: {
      id: string;
      dueDate: string;
      skipped?: boolean;
      undo?: boolean;
    }) =>
      undo ? api.unpaySub(id, dueDate) : api.paySub(id, dueDate, skipped === true),
    onSuccess: (state: MoneyState) => qc.setQueryData(moneyKey, state),
  });
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => () =>
  void qc.invalidateQueries({ queryKey: moneyKey });

export function useCreateSub() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["createSub"],
    mutationFn: (body: SubCreate) => api.createSub(body),
    onSuccess: invalidate(qc),
  });
}

export function usePatchSub() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["patchSub"],
    mutationFn: ({ id, patch }: { id: string; patch: SubPatch }) => api.patchSub(id, patch),
    onSuccess: invalidate(qc),
  });
}

export function useDeleteSub() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["deleteSub"],
    mutationFn: (id: string) => api.deleteSub(id),
    onSuccess: invalidate(qc),
  });
}
